import { Box, Checkbox, HStack, Tag, Text, VStack } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmationDialog } from "src/components/Dialogs/ConfirmationDialog";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { usePrompt } from "src/hooks/usePrompt";

export type LanguageSelectorItem = {
  language: string;
  hasTranslation: boolean;
  hasFailedTranslation: boolean;
};

/**
 * Uses usePrompt to show a language selector. confirm(items) opens the dialog
 * and returns Promise<string[] | null> (selected on Translate, null on Cancel).
 */
export function useTranslationLanguageSelector() {
  const selectionRef = useRef<LanguageSelectorItem[]>([]);

  const {
    prompt: confirm,
    resolve,
    promptProps: items,
  } = usePrompt<LanguageSelectorItem[], string[] | null>();

  const [showError, setShowError] = useState(false);

  const handleConfirm = useCallback(() => {
    if (selectionRef.current.length === 0) {
      setShowError(true);
      return;
    }
    resolve(selectionRef.current.map((item) => item.language));
  }, [resolve]);

  const handleCancel = useCallback(() => {
    setShowError(false);
    resolve(null);
  }, [resolve]);

  useEffect(() => {
    if (items) {
      setShowError(false);
      selectionRef.current = [...items];
    }
  }, [items]);

  const translationLanguageSelectorDialog = items ? (
    <ConfirmationDialog
      isOpen={true}
      header="Translate to preferred languages"
      body={
        <SelectorBody
          items={items}
          selectionRef={selectionRef}
          isError={showError}
        />
      }
      cancelButton={{ label: "Cancel" }}
      confirmButton={{ label: "Translate" }}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  ) : null;

  return {
    confirm: confirm,
    translationLanguageSelectorDialog,
  };
}

type SelectorBodyProps = {
  items: LanguageSelectorItem[];
  selectionRef: React.MutableRefObject<LanguageSelectorItem[]>;
  isError: boolean;
};

function SelectorBody({ items, selectionRef, isError }: SelectorBodyProps) {
  const [selected, setSelected] = useState<LanguageSelectorItem[]>(() => [
    ...items,
  ]);
  const getLanguageName = useGetEnglishLanguageName();

  useEffect(() => {
    const initial = [...items];
    setSelected(initial);
    selectionRef.current = initial;
  }, [items, selectionRef]);

  const toggle = useCallback(
    (toggledItem: LanguageSelectorItem) => {
      setSelected((prev) => {
        const next = prev.find((item) => item.language === toggledItem.language)
          ? prev.filter((item) => item.language !== toggledItem.language)
          : [...prev, toggledItem];
        selectionRef.current = next;
        return next;
      });
    },
    [selectionRef]
  );

  return (
    <VStack alignItems="stretch" gap={2}>
      <Text>Select the languages you want to translate your message into.</Text>
      <Box
        p={3}
        display="flex"
        flexDirection="column"
        gap={1}
        overflowY="auto"
        maxHeight="12rem"
      >
        {items.map((item) => (
          <HStack key={item.language}>
            <Checkbox
              isChecked={selected.some((s) => s.language === item.language)}
              onChange={() => toggle(item)}
            >
              {getLanguageName(item.language)}
            </Checkbox>
            {!item.hasTranslation && (
              <Tag
                size="sm"
                variant="ghost"
                backgroundColor="gray.300"
                color="gray.700"
                gap={1}
              >
                <Text fontSize="sm">Not translated</Text>
              </Tag>
            )}
            {item.hasFailedTranslation && (
              <Tag size="sm" variant="outline" colorScheme="red" gap={1}>
                <Text fontSize="sm">Translation failed</Text>
              </Tag>
            )}
          </HStack>
        ))}
      </Box>
      {isError && (
        <Text color="red.500" fontSize="sm">
          Please select at least one language.
        </Text>
      )}
    </VStack>
  );
}
